import { reactivateSubscription, upsellProduct } from '../subscription-modal/ordergroove-api-helper';

export default () => ({
  open: false,
  
  openModal() {
    this.open = true;
    document.body.classList.add('overflow-hidden');
    setTimeout(() => {
      this.addFocusTrap();
    }, 15);
  },
  
  closeModal() {
    this.open = false;
    document.body.classList.remove('overflow-hidden');
    const event = new CustomEvent('webhook-og-action-modal-closed');
    window.dispatchEvent(event);
    this.removeFocusTrap();
  },

  /**
   * Initiates an upsell process for a product based on the specified upsell type.
   * Retrieves the action token from the current URL and calls the upsell API if available.
   * 
   * @param {'one-time-upsell'|'subscription-upsell'} upsellType - The type of upsell to perform. Can be 'one-time-upsell' or 'subscription'.
   * @param {string} productId - The ID of the product to upsell.
   * @param {string} actionToken - The action token for the upsell operation.
   */
  upsellProduct(upsellType, productId, actionToken, frequencyDescription) {
    if (!actionToken || !upsellType || !productId || !frequencyDescription) {
      return;
    }

    // Call the upsell API
    // Assuming there's an API function similar to reactivateSubscription
    upsellProduct({ upsellType, productId, actionToken, frequencyDescription })
      .then(() => {
        // Show toast on successful upsell
        window.dispatchEvent(
          new CustomEvent('toast', {
            detail: { message: window.translations.ordergroove.added_to_subscription_confirmation_text, type: 'success' },
          }),
        );
        // Close modal after successful upsell
        this.closeModal();
      })
      .catch(() => {
        // Handle error case - keep modal open so user can retry
        // You might want to show an error message here
      });
  },
  
  reactivateSubscription() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const actionToken = urlParams.get('action_token');

    if (!actionToken) {
      return;
    }
    
    // // Call the reactivation API
    reactivateSubscription(actionToken).then(() => {
      // Show toast on successful reactivation
      window.dispatchEvent(
        new CustomEvent('toast', {
          detail: {
            message: window.translations.customer.global.subscription_reactivated,
            type: 'success',
          },
        }),
      );
      // Close modal after successful reactivation
      this.closeModal();
    }).catch(() => {
      // Handle error case - keep modal open so user can retry
      // You might want to show an error message here
    });
  },
  
  addFocusTrap() {
    const modal = document.querySelector('[x-data="ogActionModal"]');
    if (!modal) return;

    this.previousFocusElement = document.activeElement;
    const focusableElements = '[tabindex]:not([tabindex="-1"]), [href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
    this.modalFocusableElements = modal.querySelectorAll(focusableElements);
    
    if (this.modalFocusableElements.length === 0) return;
    
    this.firstFocusableElement = this.modalFocusableElements[0];
    this.lastFocusableElement = this.modalFocusableElements[this.modalFocusableElements.length - 1];
    
    // Focus first element
    this.firstFocusableElement.focus();
    
    // Add event listener for focus trapping
    this.focusTrapHandler = this.handleFocusTrap.bind(this);
    modal.addEventListener('keydown', this.focusTrapHandler);
  },
  
  removeFocusTrap() {
    const modal = document.querySelector('[x-data="ogActionModal"]');
    if (modal && this.focusTrapHandler) {
      modal.removeEventListener('keydown', this.focusTrapHandler);
    }
    
    // Return focus to previous element
    if (this.previousFocusElement) {
      this.previousFocusElement.focus();
    }
  },
  
  handleFocusTrap(e) {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
    const isEscPressed = e.key === 'Escape' || e.keyCode === 27;

    if (isEscPressed) {
      e.preventDefault();
      this.closeModal();
      return;
    }

    if (!isTabPressed) return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusableElement) {
        this.lastFocusableElement.focus();
        e.preventDefault();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusableElement) {
        this.firstFocusableElement.focus();
        e.preventDefault();
      }
    }
  },
  
  init() {
    // Listen for custom events to open/close modal
    window.addEventListener('open-og-action-modal', () => {
      this.openModal();
    });
    
    window.addEventListener('close-og-action-modal', () => {
      this.closeModal();
    });
    
    // Clean up event listeners
    window.addEventListener('webhook-og-action-modal-closed', () => {
      this.removeFocusTrap();
    });

    const modal = document.querySelector('.og-action-modal');
    modal.classList.remove('hidden');
    modal.classList.add('flex');
    
    // Auto-open modal if action_token is present in URL
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const actionToken = urlParams.get('action_token');
    
    if (actionToken) {
      // Auto-open the modal when the page loads with an action token
      setTimeout(() => {
        this.openModal();
      }, 100);
    }
  },
});
